import { useEffect, useMemo, useState } from 'react';
import api from '../../../lib/api';
import { useAuth } from '../../../context/AuthContext';
import { getPrivateCustomersDescription, getPrivateCustomersLabel, isDistributorRole } from '../utils/operator';
import { TARIFA_OPTIONS } from '../../../lib/utils/tarifas';

const PrivateCustomersPage = () => {
  const { auth } = useAuth();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    notes: '',
    tarifa: '' as string | number,
    address: {
      street: '',
      city: '',
      postalCode: '',
      country: 'España'
    }
  });
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState<any | null>(null);
  const [editData, setEditData] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const isDistributor = isDistributorRole(auth.user?.role);
  const title = getPrivateCustomersLabel(auth.user?.role);

  const load = async () => {
    const res = await api.users.getPrivateCustomers();
    if (res.success) setItems(res.data || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const add = async () => {
    await api.users.addPrivateCustomer(form);
    setForm({
      name: '',
      email: '',
      phone: '',
      notes: '',
      tarifa: '',
      address: {
        street: '',
        city: '',
        postalCode: '',
        country: 'España'
      }
    });
    await load();
  };

  const startEdit = (c: any) => {
    setEditing(c);
    setEditData({
      name: c.name || '',
      email: c.email || '',
      phone: c.phone || '',
      nif: c.nif || '',
      notes: c.notes || '',
      tarifa: c.tarifa || '',
      address: {
        street: c.address?.street || '',
        city: c.address?.city || '',
        postalCode: c.address?.postalCode || '',
        country: c.address?.country || 'España'
      }
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.users.updatePrivateCustomer(editing._id || editing.id, editData);
      setEditing(null);
      await load();
    } finally {
      setSaving(false);
    }
  };

  const removeCustomer = async (c: any) => {
    if (!confirm('¿Eliminar este cliente privado?')) return;
    await api.users.deletePrivateCustomer(c._id || c.id);
    await load();
  };

  const filtered = useMemo(() => {
    if (!q) return items;
    const qq = q.toLowerCase();
    return items.filter((c: any) => [c.name, c.email, c.phone, c.nif].filter(Boolean).some((v: string) => v.toLowerCase().includes(qq)));
  }, [q, items]);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{title}</h1>
          <p className="text-sm text-gray-500 mt-1">{getPrivateCustomersDescription(auth.user?.role)}</p>
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar..." className="border rounded px-3 py-2" />
      </div>
      <div className="border rounded p-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
        <div className="flex flex-col">
          <label className="text-sm text-gray-600">{isDistributor ? 'Nombre del destinatario' : 'Nombre'}</label>
          <input className="border rounded px-3 py-2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div className="flex flex-col">
          <label className="text-sm text-gray-600">Email</label>
          <input className="border rounded px-3 py-2" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div className="flex flex-col">
          <label className="text-sm text-gray-600">Teléfono</label>
          <input className="border rounded px-3 py-2" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </div>
        <div className="flex flex-col">
          <label className="text-sm text-gray-600">País</label>
          <input
            className="border rounded px-3 py-2"
            value={form.address.country}
            onChange={(e) => setForm({ ...form, address: { ...form.address, country: e.target.value } })}
          />
        </div>
        <div className="md:col-span-2 flex flex-col">
          <label className="text-sm text-gray-600">Calle</label>
          <input
            className="border rounded px-3 py-2"
            value={form.address.street}
            onChange={(e) => setForm({ ...form, address: { ...form.address, street: e.target.value } })}
          />
        </div>
        <div className="flex flex-col">
          <label className="text-sm text-gray-600">Ciudad</label>
          <input
            className="border rounded px-3 py-2"
            value={form.address.city}
            onChange={(e) => setForm({ ...form, address: { ...form.address, city: e.target.value } })}
          />
        </div>
        <div className="flex flex-col">
          <label className="text-sm text-gray-600">CP</label>
          <input
            className="border rounded px-3 py-2"
            value={form.address.postalCode}
            onChange={(e) => setForm({ ...form, address: { ...form.address, postalCode: e.target.value } })}
          />
        </div>
        <div className="flex flex-col">
          <label className="text-sm text-gray-600">Tarifa</label>
          <select
            className="border rounded px-3 py-2"
            value={form.tarifa}
            onChange={(e) => setForm({ ...form, tarifa: e.target.value ? Number(e.target.value) : '' })}
          >
            <option value="">Usar mi tarifa</option>
            {TARIFA_OPTIONS.map(t => (
              <option key={t.value} value={t.value}>{t.shortLabel}</option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2 flex flex-col">
          <label className="text-sm text-gray-600">Notas</label>
          <input
            className="border rounded px-3 py-2"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </div>
        <button onClick={add} className="px-4 py-2 rounded bg-black text-white">Añadir</button>
      </div>
      {loading ? (
        <div className="animate-pulse h-6 w-32 bg-gray-200 rounded" />
      ) : (
        <div className="grid gap-3">
          {filtered.length === 0 && <p className="text-gray-500">Sin clientes</p>}
          {filtered.map((c: any, idx) => (
            <div key={c.id || c._id || idx} className="border rounded p-3 flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{c.name}</span>
                  {c.tarifa && (
                    <span className="text-xs font-medium bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Tarifa {c.tarifa}</span>
                  )}
                </div>
                <div className="text-sm text-gray-600">{c.email}</div>
                {(c.address?.city || c.phone) && (
                  <div className="text-xs text-gray-500">{c.phone} {c.address?.city ? `· ${c.address.city}` : ''}</div>
                )}
                {c.address?.street && (
                  <div className="text-xs text-gray-500 mt-1">
                    {c.address.street}, {c.address.postalCode} {c.address.city}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => startEdit(c)} className="text-sm underline">Editar</button>
                <button onClick={() => removeCustomer(c)} className="text-sm text-red-600 underline">Eliminar</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
          <div className="bg-white rounded-md shadow-lg w-full max-w-2xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Editar cliente</h3>
              <button onClick={() => setEditing(null)} className="text-gray-500 hover:text-black">✕</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Nombre</label>
                <input value={editData.name} onChange={(e) => setEditData({ ...editData, name: e.target.value })} className="w-full border rounded px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Email</label>
                <input value={editData.email} onChange={(e) => setEditData({ ...editData, email: e.target.value })} className="w-full border rounded px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Teléfono</label>
                <input value={editData.phone} onChange={(e) => setEditData({ ...editData, phone: e.target.value })} className="w-full border rounded px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">NIF</label>
                <input value={editData.nif} onChange={(e) => setEditData({ ...editData, nif: e.target.value })} className="w-full border rounded px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Tarifa</label>
                <select
                  value={editData.tarifa || ''}
                  onChange={(e) => setEditData({ ...editData, tarifa: e.target.value ? Number(e.target.value) : '' })}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">Usar mi tarifa</option>
                  {TARIFA_OPTIONS.map(t => (
                    <option key={t.value} value={t.value}>{t.shortLabel}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-600 mb-1">Notas</label>
                <textarea value={editData.notes} onChange={(e) => setEditData({ ...editData, notes: e.target.value })} className="w-full border rounded px-3 py-2" rows={3} />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-600 mb-1">Calle</label>
                <input value={editData.address.street} onChange={(e) => setEditData({ ...editData, address: { ...editData.address, street: e.target.value } })} className="w-full border rounded px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Ciudad</label>
                <input value={editData.address.city} onChange={(e) => setEditData({ ...editData, address: { ...editData.address, city: e.target.value } })} className="w-full border rounded px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">CP</label>
                <input value={editData.address.postalCode} onChange={(e) => setEditData({ ...editData, address: { ...editData.address, postalCode: e.target.value } })} className="w-full border rounded px-3 py-2" />
              </div>
              <div className="md:col-span-4">
                <label className="block text-sm text-gray-600 mb-1">País</label>
                <input value={editData.address.country} onChange={(e) => setEditData({ ...editData, address: { ...editData.address, country: e.target.value } })} className="w-full border rounded px-3 py-2" />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded border">Cancelar</button>
              <button onClick={saveEdit} disabled={saving} className="px-4 py-2 rounded bg-black text-white">{saving ? 'Guardando...' : 'Guardar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrivateCustomersPage;

